import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Button, InputAdornment, TextField, Typography, useMediaQuery } from '@mui/material'
import { ButtonIconSeeker, IconSeeker } from '@ui-seeker/core'
import { copyToClipboard } from '@ui-seeker/utils'

import { AppAntSwitch, AppCardTitle, AppInfoButton, AppNote, AppPageTabs, AppPillToggle, AppTableSkeleton, NoDataFound, headerIconButtonSx, secondaryButtonSx } from '@components'
import { BACKEND_APIS, BI_ROUTES, USER_ROUTES } from '@constants'
import { BUSINESS_LOGISTICS_ROUTES } from '@constants/routes'
import { useToast } from '@hooks'
import { slaveId, userData } from '@signals/user'
import { dsColorNew } from '@palette'
import PageWrapper from 'Pages/@PageWrapper'
import { pickCurrency } from '@utils/currencyDefault'

import ViewBand, { RangeField, ViewToggle } from '../../@components/ViewBand'
import CarsField from '../../@components/CarsField'
import MarketBar, { TOOL_HEIGHT, toolFieldSx } from '../../@components/MarketBar'
import IdentityChip, { NoIdentityNote } from '../../@components/IdentityChip'
import useDashboardOptions from '../../@hooks/useDashboardOptions'
import useDashboardData from '../../@hooks/useDashboardData'
import { addDays, daysBetween, fmtDay, fmtRange, fmtWeekday, fromIso, toIso } from '../../@services/dates'
import { resolveRange } from '../../@services/ranges'
import { carsLabels, carsOn, shortList } from '../../@services/cars'
import { providerOptions, tierCoverage, visibleProviderIds } from '../../@services/market'
import { explorerUrl, networkExplorerForm } from '../../@services/explorerLink'
import { seasonUrl } from '../../@services/seasonLink'
import { resolveView, shownLengths, storageKey, uiStorageKey, viewToParams, viewToStored } from './@services/viewState'
import { captureOptions, compareOptions, dayColumnsFit, fitCompare, maxCaptureDays, rememberSize, requestPlan, resolveCapture, sizeFor } from './@services/capture'
import { buildRequestBody } from './@services/request'
import { buildNetworkModel, buildProviderModel } from './@services/model'
import { TYPE_LABEL, groupedView, nextSort } from './@services/groups'
import { marketMoves, rankings } from './@services/rankings'
import { excelBody } from './@services/excelRows'
import { CapturedField, CompareField, LengthsField, OfficesField, SitesField } from './@components/BandFields'
import NetworkGrid from './@components/NetworkGrid'
import ProviderGrid from './@components/ProviderGrid'
import Rankings from './@components/Rankings'
import DistanceLegend from './@components/DistanceLegend'
import NetworkDetailDialog from './@components/NetworkDetailDialog'
import RowsMenu from './@components/RowsMenu'
import LeadingArrivalNote from './@components/LeadingArrivalNote'


const cardSx = { backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '16px', boxShadow: '0 1px 2px rgba(12,26,62,.04)', p: 2 }
const STAT_LABEL = { min: 'Min', avg: 'Avg', max: 'Max' }
const VIEW_TABS = mode => [
	{ value: 'length', label: 'Rental length' }, { value: 'pickup', label: 'Pick-up date' },
	{ value: 'provider', label: mode === 'site' ? 'Website' : 'Provider' },
]

const readJson = key => {
	try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null }
}
const toastBody = (title, lines = []) => <Box>
	<Box sx={{ fontWeight: 500, color: dsColorNew.textTitle }}>{title}</Box>
	{lines.map((l, i) => <Box key={i} sx={{ fontSize: 13, color: dsColorNew.textMuted, mt: 0.25 }}>{l}</Box>)}
</Box>

/** The comparison date as the band says it under "Compare with" */
function compareUnder(cmp, capture, pickup) {
	if (!capture) return null
	if (cmp === 'day') return fmtWeekday(addDays(capture.from, -1))
	if (cmp === 'week') return capture.days === 1 ? fmtWeekday(addDays(capture.from, -7)) : fmtRange(addDays(capture.from, -7), addDays(capture.to, -7))
	const back = cmp === 'year' ? d => addDays(d, -364) : d => new Date(d.getFullYear() - 1, d.getMonth(), d.getDate())
	return `${capture.days === 1 ? fmtWeekday(back(capture.from)) : fmtRange(back(capture.from), back(capture.to))} · ${cmp === 'year' ? 'same weekday' : 'same date'}${pickup ? ` · pick-ups ${fmtRange(back(pickup.from), back(pickup.to))}` : ''}`
}

/**
 * BI dashboard "My office network" (CU-869fmjzrh): in which offices of my network am I out of the market today, and for
 * which rental lengths or pick-up dates? Opens with an answer (the whole network by country, yesterday's captures
 * against the day before), recomputes on every change of the band, and hands any cell to Season or Advanced (BI).
 * Replaces Leading Analyzer.
 */
export default function OfficeNetworkPage() {
	const { showToast } = useToast()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const uid = slaveId.value || userData.value?.uid
	const today = useMemo(() => new Date(), [])
	const options = useDashboardOptions()
	// one toolbar row (Season's polish): the pieces shrink instead of wrapping when the card is wide enough
	const oneRowToolbar = useMediaQuery('(min-width:1280px)')

	const [view, setView] = useState(null)
	const [arrival, setArrival] = useState(null)
	const [ui, setUi] = useState(() => readJson(uiStorageKey(uid)) || { collapsed: {}, rankings: true })
	const [rangeOpen, setRangeOpen] = useState(false)
	const [search, setSearch] = useState('')
	const [detail, setDetail] = useState(null)
	const [tierOptions, setTierOptions] = useState(null)
	// the size of the network decides what fits (D5); remembered per user so a reload already knows it (QA 2)
	// per office filter (QA re-check); an arrival from Leading starts from nothing remembered
	const [sizes, setSizes] = useState(() => (searchParams.get('from_leading') ? {} : readJson(uiStorageKey(uid))?.networkSizes || {}))
	const [fitPending, setFitPending] = useState(false)
	const [fitNote, setFitNote] = useState(null)
	const [errorBody, setErrorBody] = useState(null)
	const [excelBusy, setExcelBusy] = useState(false)

	// CA 4: the URL, then the last view, then the defaults; an old Leading link says what it brought (D15)
	useEffect(() => {
		if (view) return
		if (searchParams.get('from_leading')) {
			setArrival({ drop: (searchParams.get('drop') || '').split(',').filter(Boolean), lcur: searchParams.get('lcur'), lbrand: searchParams.get('lbrand'), askedBy: searchParams.get('by') })
			const v = resolveView(searchParams)
			setFitPending(v.cap === 'fixed')
			setView(v)
			return
		}
		const v = resolveView(searchParams, readJson(storageKey(uid)))
		// a copied link with fixed capture dates is trimmed to what the network allows before it is asked (QA 3)
		setFitPending(v.cap === 'fixed' && !!searchParams.get('cap'))
		setView(v)
	}, [])

	useEffect(() => {
		if (!view) return
		setSearchParams(viewToParams(view), { replace: true })
		try { localStorage.setItem(storageKey(uid), JSON.stringify(viewToStored(view))) } catch {}
	}, [view && viewToParams(view)])

	useEffect(() => {
		try { localStorage.setItem(uiStorageKey(uid), JSON.stringify(ui)) } catch {}
	}, [JSON.stringify(ui)])

	const update = patch => setView(v => ({ ...v, ...patch }))

	// what the band resolves to, and the bounds of the server said before asking (D5)
	const currency = pickCurrency(options.currencies, null, { fallbackToFirst: true })
	const pickup = view ? resolveRange(view.range, today, { from: fromIso(view.from), to: fromIso(view.to) }) : null
	const viewCapture = view ? resolveCapture(view.cap, today, { from: fromIso(view.cfrom), to: fromIso(view.cto) }) : null
	const pickupDays = pickup ? daysBetween(pickup.from, pickup.to) + 1 : 0
	const networkSize = view ? sizeFor(sizes, view) : 0
	// what the request may ask while the network size is unknown, and what the view becomes once it is (QA 2-3)
	const plan = view ? requestPlan({ v: view.v, by: view.by, pickupDays, capture: viewCapture, networkSize, fitPending }) : null
	const capture = plan?.capture || viewCapture
	const cmp = view && capture ? fitCompare(view.cmp, capture.days) : view?.cmp
	const dayFits = networkSize > 0 && dayColumnsFit(networkSize, pickupDays)
	const by = plan?.by || view?.by
	const lengths = view ? shownLengths(view, options.lors) : []
	const effective = view ? { ...view, cmp, by } : null
	const body = effective && pickup && capture && !options.loading
		? buildRequestBody({ view: effective, pickup, capture, currency: currency?.value, shownLengths: lengths }) : null
	const { data, loading, error, retry } = useDashboardData(body)

	useEffect(() => {
		if (!data?.counts) return
		// the answer is for the filter asked (latest request only)
		const next = rememberSize(sizes, view, data.counts.offices)
		setSizes(next)
		setUi(u => ({ ...u, networkSizes: next, networkSize: undefined }))
	}, [data])
	useEffect(() => { if (view && cmp && cmp !== view.cmp) update({ cmp }) }, [cmp])
	// Day that does not fit becomes Week in the view itself, so the link and a reload ask Week; Day stays disabled
	useEffect(() => { if (plan?.persistBy && view.by !== plan.persistBy) update({ by: plan.persistBy }) }, [plan?.persistBy])
	// a fixed capture asked by a link, trimmed once the size is known, with its notice
	useEffect(() => {
		if (!plan?.fit) return
		setFitPending(false)
		if (plan.fit.cut) {
			update({ cfrom: toIso(plan.fit.from), cto: toIso(plan.fit.to) })
			setFitNote(`Prices captured: the last ${plan.fit.days} day${plan.fit.days === 1 ? '' : 's'} of your link's ${plan.fit.asked} — up to ${plan.fit.days} fit with ${networkSize} offices.`)
		}
	}, [plan?.fit && `${toIso(plan.fit.from)}-${plan.fit.cut}`])
	// the reason of a 400, read once; a Day that the server finds too long falls back to Week
	useEffect(() => {
		let alive = true
		setErrorBody(null)
		if (error?.status === 400 && typeof error.json === 'function') {
			error.json().then(b => {
				if (!alive) return
				setErrorBody(b || {})
				if (b?.code === 'view_too_long' && view?.v === 'pickup' && by === 'day') update({ by: 'week' })
			}).catch(() => alive && setErrorBody({}))
		}
		return () => { alive = false }
	}, [error])

	// Tier / Providers (Season decision 8): who counts as the market, on the rows already received, brand mode only
	const meAs = view?.me || 'brand'
	const identity = data?.identity || null
	const tier = view?.tier && tierOptions ? tierOptions.find(t => Number(t.value) === Number(view.tier)) || null : null
	const tierProviders = tier ? (tier.providers || []).map(Number) : null
	const picked = view?.tier ? [] : view?.providers || []
	const marketActive = meAs === 'brand' && (!!tier || !!picked.length)
	const providersOfAnswer = meAs === 'brand' ? (data?.who || []) : []
	const visible = useMemo(() => (marketActive
		? visibleProviderIds({ providers: providersOfAnswer, identity: identity ? { primaryId: identity.primaryId } : null, tierProviders, picked })
		: null), [data, tier?.value, picked.join(','), marketActive])

	// the lengths view hides the lengths without prices while the user has not chosen them (D6)
	const shownData = useMemo(() => {
		if (data?.view === 'pickup' && data.showBy === 'day') {
			// a day column says "Mon 26 Oct" (the server sends its ISO date)
			return { ...data, columns: data.columns.map(c => { const d = fromIso(c.dates?.[0]); return d ? { ...c, label: fmtDay(d, false), sub: fmtWeekday(d, false).split(' ')[0] } : c }) }
		}
		if (!data || data.view === 'pickup' || view?.lens || !data.lengths) return data
		const priced = new Set(data.lengths.filter(l => l.withPrices).map(l => String(l.lor)))
		return priced.size ? { ...data, columns: data.columns.filter(c => priced.has(c.key)) } : data
	}, [data, view?.lens])
	const withoutPrices = data?.lengths && !view?.lens ? data.lengths.filter(l => !l.withPrices).map(l => l.lor) : []

	const showing = view?.v === 'provider' ? 'provider' : data?.view === 'pickup' ? 'pickup' : 'length'
	const opts = { stat: view?.stat, perDay: view?.price === 'day', meAs, visible, lor: view?.lor }
	const model = useMemo(() => (shownData && !shownData.noResults && view?.v !== 'provider' ? buildNetworkModel(shownData, opts) : null), [shownData, JSON.stringify({ ...opts, visible: visible && [...visible] })])
	const providerModel = useMemo(() => (shownData && !shownData.noResults && view?.v === 'provider' ? buildProviderModel(shownData, { ...opts, columnKey: String(view.lor) }) : null), [shownData, view?.v, JSON.stringify({ ...opts, visible: visible && [...visible] })])
	const mode = identity ? (meAs === 'site' ? 'site' : 'brand') : 'market'
	const sort = { key: view?.sort || 'name', dir: view?.dir || 'asc' }
	const groups = useMemo(() => {
		const m = model || (providerModel && { rows: providerModel.rows, columns: [], mode })
		return m ? groupedView(m, { by: view.grp, sort: model ? sort : null, collapsed: ui.collapsed }) : []
	}, [model, providerModel, view?.grp, sort.key, sort.dir, JSON.stringify(ui.collapsed)])
	const ranks = model ? rankings(model) : null
	const moves = model ? marketMoves(model) : null

	const code = data?.currency?.code || currency?.label || 'EUR'
	// the brand's name comes with the identity: in website mode `who` are sites (QA 1)
	const brand = identity && (identity.primaryName || (data?.who || []).find(w => Number(w.id) === Number(identity.primaryId))?.name)
	const captureText = capture ? (capture.days === 1 ? fmtWeekday(capture.from, false) : fmtRange(capture.from, capture.to)) : ''
	const sinceText = view && capture ? compareUnder(cmp, capture, null)?.split(' · ')[0] : ''
	const compact = showing === 'pickup' && by === 'day'

	const goSettings = (tab = 'account') => {
		showToast.info(toastBody(`Opening Settings › ${tab === 'account' ? 'Account' : tab === 'office_group' ? 'Office group' : 'Site group'}`))
		navigate(`${USER_ROUTES.SETTINGS_PATH}?tab=${tab}`)
	}
	const onMeAs = me => {
		if (me === 'site' && !(identity?.siteIds || []).length) {
			showToast.info(<Box>
				{toastBody('No website of yours yet', ['Add your websites, one per country, in Settings › Account › Identity. Then "Website" compares your site in each office\'s country with the other websites.'])}
				<Button size="small" variant="text" onClick={() => goSettings('account')} sx={{ textTransform: 'none', p: 0, mt: 0.5, minWidth: 0 }}>Go to Settings › Account</Button>
			</Box>)
			return
		}
		update({ me, tier: null, providers: [] })
	}

	const resolvedCars = data?.cars
	const carsText = view ? shortList(carsLabels(view, { groups: resolvedCars?.groups || [] })) : ''
	const officeIds = (data?.offices || []).map(o => o.id)

	const openExplorer = (cell = null) => {
		if (!pickup || !capture || !effective) return
		const office = cell ? cell.office : null
		const column = cell ? cell.column : null
		const kind = !cell ? 'network' : showing === 'pickup' ? 'networkPickup' : 'networkLength'
		const form = networkExplorerForm({
			kind, view: effective, officeIds: office ? [office.id] : officeIds, lengths: showing === 'pickup' ? [view.lor] : column?.lor ? [column.lor] : lengths,
			siteIds: view.siteGroup ? [] : view.sites, pickup, capture, currency: currency?.value,
			columnDates: column?.dates, resolvedCars, providerIds: marketActive ? [...visible] : null,
		})
		const notes = [view.cmp === 'year' ? `${BI_ROUTES.TITLE} compares with the same date last year.` : null, view.price === 'day' ? `${BI_ROUTES.TITLE} shows totals for the rental.` : null, view.siteGroup ? 'Your site group travels as all websites.' : null].filter(Boolean)
		showToast.info(toastBody(`Opening ${BI_ROUTES.TITLE}`, [office ? `${kind === 'networkPickup' ? 'Provider × pick-up' : 'Provider × office'} · ${office.name} · ${column.label}` : `Provider × office · ${officeIds.length} offices`, ...notes]))
		window.open(explorerUrl(form, BI_ROUTES.PATH), '_blank', 'noopener')
	}

	const openSeason = ({ office, column }) => {
		const dates = column?.dates?.length ? [column.dates[0], column.dates.at(-1)] : [toIso(pickup.from), toIso(pickup.to)]
		const url = seasonUrl(BUSINESS_LOGISTICS_ROUTES.SEASON_VS_LY_PATH, {
			office: office.id, lor: column?.lor || view.lor, from: dates[0], to: dates[1], cmp: view.cmp, by: showing === 'pickup' ? by : 'week',
			stat: view.stat, cars: view.cars, carsIds: view.carsIds, tier: view.tier, providers: view.providers,
		})
		showToast.info(toastBody('Opening Season vs last year', [`${office.name} · ${column?.label || `${view.lor} days`} · fixed pick-ups · ${STAT_LABEL[view.stat]}`, 'Season uses its own capture window, up to yesterday.']))
		window.open(url, '_blank', 'noopener')
	}

	const downloadExcel = async () => {
		if (!model && !providerModel) return
		const m = model || buildNetworkModel({ ...shownData, columns: shownData.columns.filter(c => c.key === String(view.lor)) }, opts)
		const payload = excelBody(m, { storedView: viewToStored(view), view: showing, captureTo: toIso(capture.to) })
		setExcelBusy(true)
		showToast.info(toastBody('Preparing the Excel', [`${m.rows.length} offices × ${m.columns.length} columns, collapsed groups included. Two sheets: Distance to market and Prices by provider.`]))
		try {
			const res = await fetch(`${BACKEND_APIS.DASHBOARD}/office-network/excel`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
			if (!res.ok) {
				const err = await res.json().catch(() => ({}))
				throw new Error(err.error || 'Could not prepare the Excel.')
			}
			const name = (res.headers.get('content-disposition') || '').split('filename=')[1]?.replace(/"/g, '') || 'my-office-network.xlsx'
			const blob = await res.blob()
			const a = document.createElement('a')
			a.href = window.URL.createObjectURL(blob)
			a.download = name
			document.body.appendChild(a)
			a.click()
			a.remove()
			showToast.success(toastBody('Excel downloaded', [name]))
		} catch (e) {
			showToast.error(e.message || 'Could not prepare the Excel.')
		} finally {
			setExcelBusy(false)
		}
	}

	const copyLink = () => {
		try {
			copyToClipboard(`${window.location.origin}${BUSINESS_LOGISTICS_ROUTES.OFFICE_NETWORK_PATH}?${viewToParams(view)}`)
			const relative = view.range !== 'fixed' || view.cap !== 'fixed'
			showToast.success(toastBody('Link copied', [relative ? 'It keeps relative dates: opened another day, the dates move with it.' : 'It keeps these fixed dates.']))
		} catch {
			showToast.error('Could not copy to clipboard')
		}
	}

	const closeArrival = () => {
		setArrival(null)
		const p = new URLSearchParams(searchParams)
		p.delete('from_leading'); p.delete('drop')
		setSearchParams(p, { replace: true })
	}

	const detailTarget = useMemo(() => {
		if (!detail) return null
		if (model) {
			const row = model.rows.find(r => r.office.id === detail.officeId)
			return row ? { office: row.office, column: model.columns[detail.j], cell: row.cells[detail.j] } : null
		}
		if (providerModel) {
			const row = providerModel.rows.find(r => r.office.id === detail.officeId)
			return row ? { office: row.office, column: { label: `${view.lor} days`, lor: view.lor, dates: [] }, cell: row.cell } : null
		}
		return null
	}, [detail, model, providerModel])

	const actions = <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
		<AppInfoButton title="About this screen" tooltip="About this screen">
			<p>One question, answered without filling anything in: in which offices of your network are you out of the market today, and for which rental lengths or pick-up dates?</p>
			<p>Every cell compares your price with the median of your competitors there. Same prices as {BI_ROUTES.TITLE}: the ones your company has captured. Click any cell to see who is behind it.</p>
			<p><b>Coming from Leading Analyzer?</b> Its view is “Offices by pick-up date”. Here green means you are cheaper than the market and red dearer — Leading painted it the other way round.</p>
		</AppInfoButton>
		<ButtonIconSeeker icon="link" iconWeight="regular" tooltip="Copy link to this view" aria-label="Copy link to this view" sx={headerIconButtonSx} disabled={!view} onClick={copyLink}/>
		<Button variant="outlined" onClick={() => openExplorer()} disabled={!data || data.noResults} startIcon={<IconSeeker icon="sliders" sx={{ fontSize: 13 }}/>}
			sx={{ ...secondaryButtonSx, height: 40, color: dsColorNew.webBlue500, fontWeight: 600 }}>
			Open in {BI_ROUTES.TITLE}
		</Button>
	</Box>

	const ready = !!view
	const network = data?.offices || []
	const capOpts = captureOptions(networkSize)
	const cmpOpts = compareOptions(capture?.days || 1)

	return <PageWrapper head={`DS Rentacar | ${BUSINESS_LOGISTICS_ROUTES.OFFICE_NETWORK_TITLE}`} title={BUSINESS_LOGISTICS_ROUTES.OFFICE_NETWORK_TITLE}
		subtitle={BUSINESS_LOGISTICS_ROUTES.OFFICE_NETWORK_SUBTITLE} isEmpty={false} actions={actions}>
		{!!arrival && <Box sx={{ mb: 2 }}><LeadingArrivalNote drop={arrival.drop} cuts={[
			...(view?.v === 'pickup' && arrival.askedBy === 'day' && by !== 'day' && networkSize > 0 && !dayFits ? ['Shown by week: too many offices for daily columns.'] : []),
			...(fitNote ? [fitNote] : []),
		]} currencyCode={code} onClose={closeArrival}/></Box>}

		{ready && <ViewBand
			toggles={<>
				<ViewToggle label="Offices by">
					<AppPageTabs value={view.v} onChange={(_, v) => update({ v })} tabs={VIEW_TABS(meAs)} stackProps={{ sx: { mb: 0 } }}/>
				</ViewToggle>
				{view.v === 'pickup' && <ViewToggle label="Show by">
					<AppPillToggle size="small" aria-label="Show by" value={by} onChange={b => update({ by: b })} options={[
						{ value: 'day', label: 'Day', disabled: networkSize > 0 && !dayFits, title: networkSize > 0 && !dayFits ? 'Day — too many offices for daily columns: choose a country or fewer offices' : undefined },
						{ value: 'week', label: 'Week' }, { value: 'month', label: 'Month' },
					]}/>
				</ViewToggle>}
			</>}
			togglesRight={<>
				<ViewToggle label="Price per provider">
					<AppPillToggle size="small" aria-label="Price per provider" value={view.stat} onChange={stat => update({ stat })} options={[{ value: 'min', label: 'Min' }, { value: 'avg', label: 'Avg' }, { value: 'max', label: 'Max' }]}/>
				</ViewToggle>
				<ViewToggle label="Price">
					<AppPillToggle size="small" aria-label="Price" value={view.price} onChange={price => update({ price })} options={[{ value: 'total', label: 'Total' }, { value: 'day', label: 'Per day' }]}/>
				</ViewToggle>
			</>}
			rows={[
				[
					<OfficesField key="o" view={view} network={network} groupName={data?.officeGroup?.id === view.officeGroup ? data?.officeGroup?.name : null} onChange={update} onOpenSettings={() => goSettings('office_group')}/>,
					<LengthsField key="l" view={view} options={options.lors} shown={lengths} withoutPrices={withoutPrices} onChange={update}/>,
					<CarsField key="c" view={view} resolved={resolvedCars} onChange={cars => update({ ...cars })} onOpenSettings={() => goSettings('acriss_group')}/>,
					<SitesField key="s" view={view} count={meAs === 'site' ? (data?.who || []).length : null} groupName={data?.siteGroup?.id === view.siteGroup ? data?.siteGroup?.name : null} onChange={update} onOpenSettings={() => goSettings('sites_group')}/>,
				],
				[
					<RangeField key="r" value={view.range} from={view.from} to={view.to} today={today} onChange={patch => update(patch)} open={rangeOpen} onOpenChange={setRangeOpen}/>,
					<CapturedField key="cap" value={view.cap} options={capOpts} capture={capture} maxDays={maxCaptureDays(networkSize)}
						onFixed={({ from, to }) => update({ cap: 'fixed', cfrom: from, cto: to })} onChange={c => {
						if (c === 'fixed') { const y = addDays(today, -1); update({ cap: 'fixed', cfrom: toIso(y), cto: toIso(y) }); return }
						update({ cap: c })
					}}/>,
					<CompareField key="cmp" value={cmp} options={cmpOpts} under={compareUnder(cmp, capture, view.cmp.startsWith('year') ? pickup : null)} onChange={c => update({ cmp: c })}/>,
				],
			]}
		/>}

		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
			{ready && !!pickup && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', fontSize: 13, color: dsColorNew.textMuted }}>
				<Box component="span">
					<b style={{ color: dsColorNew.textTitle }}>{network.length || '…'} offices</b>
					{data?.offices && <> in {[...new Set(network.map(o => o.countryName || o.country))].slice(0, 4).join(', ')}</>}
					{' '}· {showing === 'length' ? `${(shownData?.columns || []).map(c => c.lor).join(', ')}-day rentals` : `${view.lor}-day rentals`}
					{carsOn(view) && <> · <span style={{ color: dsColorNew.appBlue, fontWeight: 600 }}>{carsText}</span></>}
					{' '}· pick-ups <b style={{ color: dsColorNew.textTitle }}>{fmtRange(pickup.from, pickup.to)}</b> · captured {captureText} vs {sinceText}
					{marketActive && <> · <span style={{ color: dsColorNew.appBlue, fontWeight: 600 }}>market: {tier?.label || 'providers chosen'}</span></>}
				</Box>
				<Box sx={{ flex: 1 }}/>
				{!!identity && !!brand && <IdentityChip brand={brand} onChange={() => goSettings('account')} meAs={meAs} onMeAs={onMeAs}/>}
			</Box>}

			{!arrival && !!fitNote && <AppNote severity="info">{fitNote}</AppNote>}
			{data?.network?.source === 'bi_list' && <AppNote severity="info">Your office list from Settings: no recent capture record for these dates.</AppNote>}
			{!!data && !identity && <NoIdentityNote onGo={() => goSettings('account')}>
				Set your brand in <b>Settings › Account</b> to see where you stand: your distance to the market and the rankings. Meanwhile each cell is the market median and how it moved.
			</NoIdentityNote>}

			{!!model && (ranks || moves) && <Box component="section" sx={cardSx}>
				<Rankings rankings={ranks} moves={moves} open={ui.rankings !== false} onToggle={() => setUi(u => ({ ...u, rankings: u.rankings === false }))}
					since={sinceText} total={model.rows.reduce((s, r) => s + (r.reason ? 0 : r.cells.length), 0)}
					scopeText={showing === 'pickup' ? `every office and pick-up ${by}` : 'every office and rental length'}
					noComparison={data?.period?.previous ? null : 'No comparison: nothing captured then.'}
					onOpen={(officeId, j) => setDetail({ officeId, j })}/>
			</Box>}

			<Box component="section" sx={cardSx}>
				<Box sx={{ mb: 1.5 }}>
					<AppCardTitle icon="table-cells" title={cardTitle(mode, showing, by)} subtitle={cardSubtitle({ ready, loading, data, model, providerModel, mode, captureText, tierName: tier?.label, marketActive })} sx={{ minWidth: 0 }}>
						<AppInfoButton title="How to read this table" tooltip="How to read this table">
							<p>Each cell is how far your price is from the market median there: (your price − median) ÷ median, in % and in {code}.</p>
							<p>Your price is the {STAT_LABEL[view?.stat || 'min']} of the prices captured for your brand over all the pick-up dates of the cell; each competitor's the same way. Market median is the median of your competitors' prices, your brands excluded.</p>
							<p>Colour: green = you are cheaper, red = dearer; the stronger, the further. Within ±2% it is “in line”, no colour. Always with sign and arrow.</p>
							<p>Groups: the summary row is the median of its offices' distances, and how many are above the market.</p>
							<p>Tier and Providers choose who counts as the market; Find an office only finds a row. Few prices (!): under 5 prices behind the cell — read it with care.</p>
						</AppInfoButton>
						<Button variant="outlined" onClick={downloadExcel} disabled={excelBusy || (!model && !providerModel)} title="All the offices of this view, collapsed groups included"
							startIcon={<IconSeeker icon="file-excel" sx={{ fontSize: 13 }}/>} sx={{ ...secondaryButtonSx, height: 36, ml: 'auto', whiteSpace: 'nowrap', flexShrink: 0 }}>
							Download Excel
						</Button>
					</AppCardTitle>
					{(!!model || !!providerModel) && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: oneRowToolbar ? 'nowrap' : 'wrap', mt: 1.5, '& > *': { minWidth: 0 } }}>
						{meAs === 'brand' && <MarketBar
							tier={tier ? Number(tier.value) : null} onTierOptions={setTierOptions} onTier={id => update({ tier: id, providers: [] })}
							options={providerOptions({ providers: providersOfAnswer.map(w => ({ ...w })), identity: identity ? { primaryId: identity.primaryId } : null })}
							shown={tier ? tierCoverage({ providers: providersOfAnswer, identity, tierProviders }).ids : picked}
							coverage={t => tierCoverage({ providers: providersOfAnswer, identity, tierProviders: (t?.providers || []).map(Number) })}
							active={marketActive} onProviders={ids => update({ tier: null, providers: ids })}
						/>}
						<TextField size="small" placeholder="Find an office" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') setSearch('') }}
							inputProps={{ 'aria-label': 'Find an office' }}
							InputProps={{ startAdornment: <InputAdornment position="start"><IconSeeker icon="magnifying-glass" sx={{ fontSize: 13, color: dsColorNew.textSubtle }}/></InputAdornment> }}
							sx={{ flex: '0 1 170px', minWidth: 110, ...toolFieldSx() }}/>
						<RowsMenu grp={view.grp} sort={sort} columns={model?.columns || []} onGroup={grp => update({ grp })} onSort={s => update({ sort: s.key, dir: s.dir })}/>
						{mode !== 'market' && <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, height: TOOL_HEIGHT, flexShrink: 0 }}>
							<AppAntSwitch checked={!!view.chg} onChange={() => update({ chg: !view.chg })} inputProps={{ 'aria-label': 'Show change' }}/>
							<Typography sx={{ fontSize: 13, color: dsColorNew.textDark, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => update({ chg: !view.chg })}>Show change</Typography>
						</Box>}
						{marketActive && <Button variant="text" onClick={() => update({ tier: null, providers: [] })} sx={{ textTransform: 'none', color: dsColorNew.textMuted, height: TOOL_HEIGHT, whiteSpace: 'nowrap', flexShrink: 0 }}>Clear filters</Button>}
					</Box>}
				</Box>

				<Box sx={{ position: 'relative' }}>
					{renderBody({
						ready, data, error, errorBody, loading, model, providerModel, groups, mode, code, sort, search, compact, view, captureText, retry,
						onSort: key => { const s = nextSort(sort, key); update({ sort: s.key, dir: s.dir }) },
						onToggleGroup: key => setUi(u => {
							const current = groups.find(g => g.key === key)?.collapsed
							return { ...u, collapsed: { ...u.collapsed, [key]: !current } }
						}),
						onOpen: (officeId, j) => setDetail({ officeId, j }),
						onLast7: capOpts.find(o => o.value === 'last7')?.disabled ? null : () => update({ cap: 'last7', cmp: view.cmp === 'day' ? 'week' : view.cmp }),
						onSettings: () => goSettings('account'),
						onExplorer: () => openExplorer(),
						onClearSearch: () => setSearch(''),
					})}
					{loading && !!data && <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 7.5, zIndex: 6 }}>
						<Box component="span" sx={{ backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '999px', px: 1.75, py: 0.75, fontSize: 13, color: dsColorNew.textMuted, boxShadow: '0 12px 32px rgba(12,26,62,.12)' }}>Updating…</Box>
					</Box>}
				</Box>

				{(!!model || !!providerModel) && <DistanceLegend mode={mode} code={code} perDay={view.price === 'day'} compact={compact}/>}
			</Box>
		</Box>

		<NetworkDetailDialog open={!!detailTarget} {...(detailTarget || {})} mode={mode} code={code} perDay={view?.price === 'day'} compareText={sinceText}
			subtitle={detailTarget ? `${[detailTarget.office.countryName, TYPE_LABEL[detailTarget.office.type]].filter(Boolean).join(' · ')} · pick-ups ${pickup ? fmtRange(pickup.from, pickup.to) : ''} · prices captured ${captureText} · ${STAT_LABEL[view.stat]} per provider · vs ${sinceText}` : ''}
			onClose={() => setDetail(null)} onSeason={() => openSeason(detailTarget)} onExplorer={() => openExplorer(detailTarget)}/>
	</PageWrapper>
}

function cardTitle(mode, showing, by) {
	const what = showing === 'pickup' ? `pick-up ${by === 'day' ? 'day' : by}` : showing === 'provider' ? (mode === 'site' ? 'website' : 'provider') : 'rental length'
	if (mode === 'market') return `Market median, by office and ${what}`
	if (mode === 'site') return `Your website vs the other websites, by office and ${what}`
	return `Your distance to the market, by office and ${what}`
}

function cardSubtitle({ ready, loading, data, model, providerModel, mode, captureText, tierName, marketActive }) {
	if (!ready || (loading && !data)) return 'Loading…'
	if (data?.noResults) return 'No prices for this view'
	if (!model && !providerModel) return ''
	const rows = (model || providerModel).rows
	const notCaptured = rows.filter(r => r.reason === 'notCaptured').length
	const parts = [`${rows.length} offices`]
	if (model) parts.push(`${model.columns.length} columns`)
	parts.push(mode === 'market' ? 'median of the providers shown' : marketActive ? `vs median of the competitors shown${tierName ? ` (${tierName})` : ''}` : 'vs median of your competitors in each office, your brands excluded')
	if (notCaptured) parts.push(`${notCaptured} with no prices on ${captureText}`)
	if (model?.thinCells) parts.push(`${model.thinCells} cells with few prices`)
	return parts.join(' · ')
}

function renderBody({ ready, data, error, errorBody, loading, model, providerModel, groups, mode, code, sort, search, compact, view, captureText, retry, onSort, onToggleGroup, onOpen, onLast7, onSettings, onExplorer }) {
	if (error && !loading) {
		const status = error?.status
		if (status === 400) {
			return <ErrorFromServer body={errorBody} onExplorer={onExplorer}/>
		}
		return <AppNote severity="error" sx={{ my: 1 }}>
			<b>Could not load the prices.</b> The server did not answer in time. Your view is kept: try again in a moment.
			<Box sx={{ mt: 1 }}><Button variant="outlined" onClick={retry} sx={secondaryButtonSx}>Try again</Button></Box>
		</AppNote>
	}
	if (!ready || !data) return <AppTableSkeleton rows={9} columns={5}/>
	if (data.officesEmpty) return <NoDataFound title="No offices in this group" text="The office group you chose has no offices, or it is not one of yours."/>
	if (data.sitesEmpty) return <NoDataFound title="No websites in this group" text="The site group you chose has no websites, or it is not one of yours."/>
	if (data.noResults) {
		return <Box>
			<NoDataFound title={`No prices captured ${view.cap === 'yday' ? 'yesterday' : 'on these days'}`} text={`Your company captured no prices for any office of this view on ${captureText}. The searches may not have run that day.`}/>
			<Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5 }}>
				{onLast7 && <Button variant="outlined" onClick={onLast7} sx={secondaryButtonSx}>Use the last 7 days</Button>}
				<Button variant="text" onClick={onExplorer} sx={{ textTransform: 'none', color: dsColorNew.textMuted }}>Open in {BI_ROUTES.TITLE}</Button>
			</Box>
		</Box>
	}
	if (providerModel) {
		return <ProviderGrid model={providerModel} groups={groups} mode={mode} code={code} search={search} perDay={view.price === 'day'} captureText={captureText}
			onToggleGroup={onToggleGroup} onOpen={onOpen} onLast7={onLast7} onSettings={onSettings} whoLabel={mode === 'site' ? 'Website' : 'Provider'}/>
	}
	return <NetworkGrid model={model} groups={groups} mode={mode} code={code} sort={sort} onSort={onSort} search={search} compact={compact}
		showChange={!!view.chg} perDay={view.price === 'day'} captureText={captureText} onToggleGroup={onToggleGroup} onOpen={onOpen} onLast7={onLast7} onSettings={onSettings}/>
}

// a 400 says why: the bounds of the view (D5), never "the server did not answer"
function ErrorFromServer({ body, onExplorer }) {
	const text = body?.error || 'This view is too long: choose fewer offices or pick-up dates.'
	return <AppNote severity="error" sx={{ my: 1 }}>
		{text}
		{body?.code === 'range_too_long' && <Box sx={{ mt: 1 }}><Button variant="text" onClick={onExplorer} sx={{ textTransform: 'none', p: 0 }}>Open in {BI_ROUTES.TITLE}</Button></Box>}
	</AppNote>
}
